'use client'

import { toHomePage } from '@/feature/account/services/account.routing'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { type VariantProps, cva } from 'class-variance-authority'
import { Button } from 'react-aria-components'
import InlineLogoWhite from './logo-inline-white.svg'
import InlineLogo from './logo-inline.svg'
import LogoCompactWhite from './logo-white.svg'
import LogoCompact from './logo.svg'

export const logoSizes = ['xs', 's', 'm', 'l', 'xl'] as const

const logoCva = cva('flex', {
  variants: {
    size: {
      xs: 'h-2 desktop:h-3',
      s: 'h-3 desktop:h-4',
      m: 'h-4 desktop:h-5',
      l: 'h-5 desktop:h-6',
      xl: 'h-6 desktop:h-7',
    },
  },
})

type LogoVariantProps = VariantProps<typeof logoCva>

export type LogoProps = WithTestId<
  LogoVariantProps & {
    className?: string
    white?: boolean
    compact?: boolean
    onPress?: () => void
  }
>

function ThemeLogo({ white, compact, className }: LogoProps) {
  let Component
  const componentProps = {
    className,
  }

  if (compact) {
    Component = white ? LogoCompactWhite : LogoCompact
  } else {
    Component = white ? InlineLogoWhite : InlineLogo
  }

  return <Component {...componentProps} />
}

export function Logo({
  className,
  size = 'm',
  white = false,
  compact = false,
  onPress,
  'data-testid': testId,
}: LogoProps): React.ReactElement {
  const { t } = useTranslation(['logo'])

  const { pushWithProgressBar } = useRouter()

  return (
    <Button
      aria-label={t('logo:title')}
      data-testid={testId}
      onPress={() => {
        pushWithProgressBar(toHomePage)
        onPress?.()
      }}
    >
      <ThemeLogo
        className={twMerge('w-auto', className, logoCva({ size }))}
        white={white}
        compact={compact}
      />
    </Button>
  )
}
